import { get } from "lodash";
import { createSelector } from "reselect";
import { formatSentence } from "../../../utils/stringUtils.js";

// -- QUERIES -- //

const successesQuery = state => get(state, "api.successes", null);
const errorMessagesQuery = state => get(state, "api.errorMessages", null);
const violationErrorsQuery = state => get(state, "api.violationErrors", null);

// -- SELECTORS -- //

export const selectLatestSuccesses = createSelector(
  [successesQuery],
  successes => {
    if (!successes || successes.length < 1) return null;

    const result = successes.reduce((acc, item) => {
      const path = get(item, "requestAction.payload.fieldPath", null);
      if (path) {
        acc[path] = item;
      }
      return acc;
    }, {});

    return result;
  },
  {
    memoizeOptions: {
      maxSize: 10
    }
  }
);

export const selectLatestErrorMessage = createSelector(
  [errorMessagesQuery],
  errorMessages => {
    if (!errorMessages) return {};
    return errorMessages[errorMessages.length - 1];
  }
);

export const selectLatestErrorMessagesByRequestType = createSelector(
  [errorMessagesQuery],
  errorMessages => requestType => {
    if (!errorMessages || errorMessages.length < 1) return null;

    const result = errorMessages.reduce((acc, item) => {
      if (item.requestAction.type === requestType) acc.push(item);
      return acc;
    }, []);

    return result;
  }
);

export const selectLatestViolationErrors = createSelector(
  [violationErrorsQuery],
  violationErrors => {
    if (!violationErrors || violationErrors.length < 1) return null;

    const result = violationErrors[violationErrors.length - 1].reduce(
      (acc, item) => {
        const path = item.propertyPath;
        const message = formatSentence(item.message);

        if (acc[path]) {
          acc[path] = `${acc[path]} ${message}`;
        } else {
          acc[path] = message;
        }
        return acc;
      },
      {}
    );

    return result;
  }
);

export const selectAllViolationErrors = createSelector(
  [violationErrorsQuery],
  violationErrors => {
    if (!violationErrors || violationErrors.length < 1) return null;

    // returning the latest errors for now
    // it may be we have to merge/combine these together at a later date
    const result = violationErrors.flat().reduce((acc, item) => {
      const path = item.propertyPath;
      const message = formatSentence(item.message);

      if (acc[path]) {
        acc[path] = `${acc[path]} ${message}`;
      } else {
        acc[path] = message;
      }
      return acc;
    }, {});

    return result;
  }
);

// using the following export pattern so we can mock more easily using jest

const errorSelector = {
  selectLatestSuccesses,
  selectLatestErrorMessage,
  selectLatestErrorMessagesByRequestType,
  selectLatestViolationErrors,
  selectAllViolationErrors
};

export default errorSelector;
